import { useState } from 'react'
import '../styles/GameSetupScreen.css'

type GameMode = 'local' | 'network' | 'ai'

interface GameSetupScreenProps {
  gameTitle: string
  gameIcon?: string
  gameBanner?: string // Optional gradient background
  supportedModes: GameMode[]
  onModeSelect: (mode: GameMode) => void
  onBack?: () => void
  children?: React.ReactNode // Custom setup content (e.g., player count)
}

const MODE_INFO = {
  local: {
    title: 'Local Play',
    icon: '🏠',
    description: 'Play on the same device with friends',
    gradient: 'var(--gradient-blue)'
  },
  network: {
    title: 'Online Multiplayer',
    icon: '🌐',
    description: 'Play with friends across the internet',
    gradient: 'var(--gradient-purple)'
  },
  ai: {
    title: 'Play vs AI',
    icon: '🤖',
    description: 'Practice against computer opponent',
    gradient: 'var(--gradient-orange)'
  }
}

export function GameSetupScreen({
  gameTitle,
  gameIcon,
  gameBanner,
  supportedModes,
  onModeSelect,
  onBack,
  children
}: GameSetupScreenProps) {
  const [selectedMode, setSelectedMode] = useState<GameMode | null>(null)

  const handleModeClick = (mode: GameMode) => {
    setSelectedMode(mode)
    // Small delay for visual feedback
    setTimeout(() => onModeSelect(mode), 150)
  }

  return (
    <div className="game-setup-screen">
      {/* Banner */}
      <div
        className="game-setup-banner"
        style={{ background: gameBanner || 'var(--gradient-bg-primary)' }}
      >
        {gameIcon && <div className="game-setup-banner-icon">{gameIcon}</div>}
        <h1 className="game-setup-banner-title">{gameTitle}</h1>
        {onBack && (
          <button className="game-setup-back-btn" onClick={onBack}>
            ← Back
          </button>
        )}
      </div>

      {/* Mode Selection */}
      <div className="game-setup-content">
        <div className="game-setup-section">
          <h2 className="game-setup-section-title">Choose Game Mode</h2>

          <div className="game-mode-grid">
            {supportedModes.map((mode) => {
              const info = MODE_INFO[mode]
              const isSelected = selectedMode === mode

              return (
                <button
                  key={mode}
                  className={`game-mode-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleModeClick(mode)}
                  style={{
                    '--mode-gradient': info.gradient
                  } as React.CSSProperties}
                >
                  <div className="game-mode-icon">{info.icon}</div>
                  <h3 className="game-mode-title">{info.title}</h3>
                  <p className="game-mode-description">{info.description}</p>
                  {isSelected && <div className="game-mode-checkmark">✓</div>}
                </button>
              )
            })}
          </div>
        </div>

        {/* Custom Content (e.g., player count, difficulty) */}
        {children && (
          <div className="game-setup-section">
            {children}
          </div>
        )}
      </div>
    </div>
  )
}
